import type { GraphData } from '../../../../utils/exportUtils';
import type { GraphConfig, SelectedGraphs } from '../../../SharedComponents/ExportDialog';
import type { PercentageDatum, QuartileData } from './RenterDataReport.utils';

type RenterDataExportData = {
  medianHouseholdIncome: { value: number | null; delta: number };
  population: { value: number | null; delta: number };
  percentRenters: { value: number | null; delta: number };
  petOwnership: Array<PercentageDatum>;
  bedroomsSearched: Array<PercentageDatum>;
  bathroomsSearched: Array<PercentageDatum>;
  rentersMovedFrom: Array<PercentageDatum>;
  rentersMovedTo: Array<PercentageDatum>;
  rentQuartiles: Array<QuartileData> | null;
  annualIncome: Array<PercentageDatum>;
  topAmenitySearches: Array<PercentageDatum>;
  topKeywordSearches: Array<PercentageDatum>;
};

export const getRenterDataGraphConfigs = (): Array<GraphConfig> => [
  { id: 'summary-cards', label: 'Summary metrics' },
  { id: 'pet-ownership', label: 'Pets searched' },
  { id: 'bedrooms-searched', label: 'Bedrooms searched' },
  { id: 'bathrooms-searched', label: 'Bathrooms searched' },
  { id: 'renters-moved-from', label: 'Top 10 cities renters moved from' },
  { id: 'renters-moved-to', label: 'Top 10 cities renters moved to' },
  { id: 'affordable-rent-ranges', label: 'Affordable rent ranges' },
  { id: 'annual-income', label: 'Annual renter income' },
  { id: 'top-amenity-searches', label: 'Top amenity searches' },
  { id: 'top-keyword-searches', label: 'Top keyword searches' },
];

const formatCurrency = (value: number): string => {
  return `$${value.toLocaleString()}`;
};

const formatNumber = (value: number): string => {
  return value.toLocaleString();
};

const extractSummaryCardsData = (data: RenterDataExportData): GraphData => {
  return {
    name: 'Summary_Metrics',
    data: [
      {
        Metric: 'Median household income',
        Value: data.medianHouseholdIncome.value !== null ? formatCurrency(data.medianHouseholdIncome.value) : '—',
        Change: data.medianHouseholdIncome.value !== null ? formatCurrency(data.medianHouseholdIncome.delta) : '',
      },
      {
        Metric: 'Population',
        Value: data.population.value !== null ? formatNumber(data.population.value) : '—',
        Change: data.population.value !== null ? formatNumber(data.population.delta) : '',
      },
      {
        Metric: 'Percentage who rent',
        Value: data.percentRenters.value !== null ? `${data.percentRenters.value}%` : '—',
        Change: data.percentRenters.value !== null ? `${data.percentRenters.delta}%` : '',
      },
    ],
    columns: ['Metric', 'Value', 'Change'],
  };
};

const extractPercentageData = (name: string, data: Array<PercentageDatum>): GraphData => {
  return {
    name,
    data: data.map((item) => ({
      Category: item.label,
      Percentage: item.value,
    })),
    columns: ['Category', 'Percentage'],
  };
};

export const extractRenterData = (data: RenterDataExportData, selectedGraphs: SelectedGraphs): Array<GraphData> => {
  const graphsData: Array<GraphData> = [];

  if (selectedGraphs['summary-cards']) {
    graphsData.push(extractSummaryCardsData(data));
  }

  if (selectedGraphs['pet-ownership']) {
    graphsData.push(extractPercentageData('Pet_Ownership', data.petOwnership));
  }

  if (selectedGraphs['bedrooms-searched']) {
    graphsData.push(extractPercentageData('Bedrooms_Searched', data.bedroomsSearched));
  }

  if (selectedGraphs['bathrooms-searched']) {
    graphsData.push(extractPercentageData('Bathrooms_Searched', data.bathroomsSearched));
  }

  if (selectedGraphs['renters-moved-from']) {
    graphsData.push(extractPercentageData('Renters_Moved_From', data.rentersMovedFrom));
  }

  if (selectedGraphs['renters-moved-to']) {
    graphsData.push(extractPercentageData('Renters_Moved_To', data.rentersMovedTo));
  }

  if (selectedGraphs['affordable-rent-ranges'] && data.rentQuartiles) {
    graphsData.push({
      name: 'Affordable_Rent_Ranges',
      data: data.rentQuartiles.map((item) => ({
        Quartile: item.label,
        'Rent Range': item.value,
      })),
      columns: ['Quartile', 'Rent Range'],
    });
  }

  if (selectedGraphs['annual-income']) {
    graphsData.push(extractPercentageData('Annual_Renter_Income', data.annualIncome));
  }

  if (selectedGraphs['top-amenity-searches']) {
    graphsData.push(extractPercentageData('Top_Amenity_Searches', data.topAmenitySearches));
  }

  if (selectedGraphs['top-keyword-searches']) {
    graphsData.push(extractPercentageData('Top_Keyword_Searches', data.topKeywordSearches));
  }

  return graphsData;
};
